/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import { Query } from '@/gql/graphql'
import { gql, useQuery } from '@apollo/client'
import Select from '@atlaskit/select'
import { Formik, type FormikHelpers, type FormikProps } from 'formik'
import { isEqual } from 'lodash'
import { useMemo, type FC } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import { StoreRegionEnum } from '../../helpers'
import TextEditor from '../TextEditor'

const GET_STORES_QUERY = gql`
  query {
    stores(isVirtual: false, limit: 100) {
      id
      name
      region
      store
      address
    }
  }
`

export const validationSchema = yup.object().shape({
  id: yup.string(),
  name: yup.string()
    .min(3, 'O nome deve ter no mínimo 3 caracteres')
    .max(255, 'O nome deve ter no máximo 255 caracteres')
    .required('O nome é obrigatório')
    .default(''),
  description: yup.string()
    .min(3, 'A descrição deve ter no mínimo 3 caracteres')
    .max(2000, 'A descrição deve ter no máximo 255 caracteres')
    .default(''),
  activities: yup.string()
    .min(3, 'As atividades devem ter no mínimo 3 caracteres')
    .max(2000, 'As atividades devem ter no máximo 255 caracteres')
    .default(''),
  requirements: yup.string()
    .min(3, 'Os requisitos devem ter no mínimo 3 caracteres')
    .max(2000, 'Os requisitos devem ter no máximo 255 caracteres')
    .default(''),
  stores: yup.array().of(
    yup.object().shape({
      id: yup.string()
    }).nullable()
  ).nullable(),
  status: yup.string().oneOf(['active', 'inactive', 'deleted']).default('active'),
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormWorkWithUsHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormWorkWithUsHandleSubmit,
  initialValues?: Partial<ValidationSchema>
}

const FormWorkWithUs: FC<FormikProps<ValidationSchema>> = ({
  handleSubmit,
  handleChange,
  handleBlur,
  isSubmitting,
  touched,
  setFieldValue,
  isValid,
  values,
  errors,
  initialValues = {},
}) => {
  const { data, loading } = useQuery<Query>(GET_STORES_QUERY, {
    fetchPolicy: 'network-only'
  })
  const navigate = useNavigate()
  const isChanged: boolean = useMemo(() => {
    return !isEqual(values, initialValues)
  }, [values, initialValues])

  return (
    <Row>
      <Col md={12} className='mb-3'>
        <form onSubmit={handleSubmit}>
          <Row>
            <Form.Group as={Col} md={12} className="mb-3">
              <Form.Label>Código da vaga</Form.Label>
              <Form.Control
                type='text'
                name='id'
                value={values.id?.toString().padStart(10, '0') ?? 'O id será gerado automaticamente'}
                placeholder='O id será gerado automaticamente'
                onChange={handleChange}
                onBlur={handleBlur}
                disabled
              />
              {errors.id && touched.id && <p>{errors.id}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className="mb-3">
              <Form.Label>Nome da vaga</Form.Label>
              <Form.Control
                type='text'
                name='name'
                value={values.name ?? ''}
                placeholder='Descrição do local do banner'
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
              />
              {errors.name && touched.name && <p>{errors.name}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className='mb-3'>
              <Form.Label>Visibilidade</Form.Label>
              <Form.Select
                value={values.status}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
                name="status"
              >
                <option>Selecione uma opção</option>
                <option value='active'>🌎 Pública</option>
                <option value='inactive'>🔒 Privada</option>
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md={12} className='mb-3'>
              <Form.Label>Informe as lojas associadas a vaga</Form.Label>
              <Select
                options={data?.stores?.map(store => ({
                  label: `${StoreRegionEnum[store?.region ?? 'unknown']} - ${store?.name}`,
                  value: store?.id
                })) || []}
                value={data?.stores?.filter(store => values.stores?.some(s => s?.id === store?.id)).map(store => ({
                  label: `${StoreRegionEnum[store?.region ?? 'unknown']} - ${store?.name}`,
                  value: store?.id
                })) || []}
                onChange={selectedOptions => {
                  const selectedStores = selectedOptions.map(option => {
                    const store = data?.stores?.find(store => store?.id === option.value)
                    return store ? { id: store.id } : null
                  }).filter(store => store !== null) as { id: string }[]
                  setFieldValue('stores', selectedStores)
                }}
                onBlur={handleBlur}
                isDisabled={isSubmitting || loading}
                name="stores"
                isMulti
                isSearchable={false}
                placeholder='Selecione uma ou mais lojas'
              />
            </Form.Group>
            <Form.Group as={Col} md={12} className="mb-3">
              <Form.Label>Descrição da vaga</Form.Label>
              <TextEditor
                value={values?.description ?? ''}
                placeholder='Escreva aqui a descrição da vaga'
                readOnly={isSubmitting}
                onChange={value => setFieldValue('description', value)}
              />
              {errors.description && <p>{errors.description}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className="mb-3">
              <Form.Label>Atividades da vaga</Form.Label>
              <TextEditor
                value={values?.activities ?? ''}
                placeholder='Escreva aqui as atividades da vaga'
                readOnly={isSubmitting}
                onChange={value => setFieldValue('activities', value)}
              />
              {errors.activities && <p>{errors.activities}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className="mb-3">
              <Form.Label>Requisitos da vaga</Form.Label>
              <TextEditor
                value={values?.requirements ?? ''}
                placeholder='Escreva aqui as atividades da vaga'
                readOnly={isSubmitting}
                onChange={value => setFieldValue('requirements', value)}
              />
              {errors.requirements && <p>{errors.requirements}</p>}
            </Form.Group>
            <Col md={12}>
              <Button
                type="submit"
                variant='primary'
                disabled={!isValid || isSubmitting || !isChanged}
              >
                Salvar
              </Button>
              <Button
                variant="secondary"
                style={{ marginLeft: 10 }}
                onClick={() => navigate('/work-with-us')}
                disabled={isSubmitting}
              >
                Voltar
              </Button>
            </Col>
          </Row>
        </form>
      </Col>
    </Row>
  )
}

const FormWorkWithUsWrapper: FC<Props> = ({ onSubmit = () => {}, initialValues = {} }) => (
  <Formik
    component={FormWorkWithUs}
    validateOnChange
    validateOnBlur
    validateOnMount
    isInitialValid
    enableReinitialize
    validationSchema={validationSchema}
    initialValues={validationSchema.cast(initialValues)}
    onSubmit={onSubmit}
  />
)

export default FormWorkWithUsWrapper
